/**
 * Design System — Categorical chart-series palette (data-visualisation tokens).
 *
 * MF-34 / G05 DS-10: the admin had NO charting library and no governed chart
 * colour registry. These are the token source for the `Chart` façade
 * (frontend/shared/components/Chart.tsx). They are emitted by the ThemeProvider
 * as `--color-chart-1 … --color-chart-8`, so charts stay theme-aware (light/dark)
 * and components never hardcode a hex (MF-20 governance).
 *
 * COLOUR-BLIND SAFETY: hues are based on the Okabe–Ito / Paul-Tol qualitative
 * palettes (designed to be distinguishable under protanopia / deuteranopia /
 * tritanopia). Each entry is additionally tuned to clear WCAG ≥3:1 against its
 * own mode's canvas so a solid mark reads against the background — locked by
 * ChartPalette.test.ts. A data-table fallback (Chart's `<table>`) provides the
 * non-colour path required for full a11y.
 *
 * NOTE: presentation-only. No business logic here.
 */

/** How many distinct series colours are emitted; the Chart façade cycles past this. */
export const chartSeriesCount = 8;

/** Light-mode series — tuned to read on the light canvas (neutral[50] / #ffffff). */
export const chartSeriesLight: readonly string[] = [
  '#1f6fb2', // blue
  '#d55e00', // vermillion
  '#158463', // bluish green
  '#9b3d8f', // reddish purple
  '#b26b00', // amber / orange (darkened for light-bg contrast)
  '#2b7a9e', // sky-blue (darkened for light-bg contrast)
  '#7a6f00', // gold / olive (darkened yellow for light-bg contrast)
  '#555b66', // slate (neutral series)
];

/** Dark-mode series — brighter members that read on the dark canvas (neutral[900]). */
export const chartSeriesDark: readonly string[] = [
  '#56b4e9', // sky blue
  '#f0a45c', // orange
  '#4ade80', // green
  '#e79ecb', // pink-purple
  '#ff9e6d', // light vermillion
  '#7fb3ff', // light blue
  '#f0e442', // yellow
  '#b6bdc9', // light slate (neutral series)
];
